import React from 'react'; // Import React if using icons
// Import teaser demo components
import { AIPortfolioStrategistDemo } from '../components/teaser-demos/AIPortfolioStrategistDemo';
import { SmartCapitalReallocationDemo } from '../components/teaser-demos/SmartCapitalReallocationDemo';
import { StrategyXRayDemo } from '../components/teaser-demos/StrategyXRayDemo';
import { MonteCarloSimulationDemo } from '../components/teaser-demos/MonteCarloSimulationDemo';
import { ActionMonitoringDemo } from '../components/teaser-demos/ActionMonitoringDemo';
import { AlertCenterDemo } from '../components/teaser-demos/AlertCenterDemo';
import { TradingActivityFeedDemo } from '../components/teaser-demos/TradingActivityFeedDemo';
import { HealthAnalyticsDashboardDemo } from '../components/teaser-demos/HealthAnalyticsDashboardDemo';
import { ForecastConfigurationDemo } from '../components/teaser-demos/ForecastConfigurationDemo';
import { HistoricalProjectionDemo } from '../components/teaser-demos/HistoricalProjectionDemo';
import { InteractiveEfficientFrontierDemo } from '../components/teaser-demos/InteractiveEfficientFrontierDemo';
import { LifecycleVisualizationDemo } from '../components/teaser-demos/LifecycleVisualizationDemo';
import { mockSwapData, mockOptimizationData, mockStrategyHealthData, mockMonteCarloData } from '../data/teaserMockData';
// Example: Import specific icons if you plan to use them
// import { CogIcon, FireIcon, LightBulbIcon, CubeTransparentIcon, ShieldExclamationIcon, BeakerIcon, ChartBarIcon, DocumentTextIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

// Base props interface for all teaser demo components
export interface TeaserDemoProps {
  className?: string;
  aspectRatio?: 'video' | 'square'; // For responsive behavior
  isLoading?: boolean; // Add loading state support
  onError?: (error: Error) => void; // Error callback
  [key: string]: any; // Allow additional props
}

export interface TeaserSubFeature {
  title: string;
  description: string;
  imageUrl?: string; // Make optional for backward compatibility - serves as fallback for errors
  component?: React.ComponentType<TeaserDemoProps>; // Add component support
  mockData?: TeaserDemoProps; // Props for the component
  icon?: React.ElementType; // Optional: Icon component for the sub-feature
}

interface TeaserFeatureContent {
  // heroBannerUrl: string; // Removed
  title: string;  // Main title for the page
  description: string;  // Main introductory paragraph for the page
  subFeatures: TeaserSubFeature[];
}

// Example content - Add content for each featureName you use in routeConfig
export const teaserContent: Record<string, TeaserFeatureContent> = {
  'Portfolio Command Center': {
    // heroBannerUrl removed
    title: 'Command Your Portfolio with AI-Powered Intelligence', // Use placeholder for feature name
    description: 'Transform your trading with an AI strategist that automatically identifies winners to promote, losers to demote, and executes strategic swaps between accounts. The {featureName} delivers institutional-grade portfolio management. Requires {minPlan} plan or higher.', // Use {minPlan}
    subFeatures: [
      {
        title: 'AI Portfolio Strategist',
        description: 'Get AI-driven recommendations to promote winners, demote losers, and execute strategic swaps between accounts. Watch your portfolio automatically optimize itself.',
        imageUrl: '/images/teasers/command-center-manage.jpg', // Fallback image
        component: AIPortfolioStrategistDemo,
        mockData: {
          recommendations: mockSwapData.strategies.slice(0, 2),
          symbol: mockSwapData.symbol
        }
      },
      {
        title: 'Action Monitoring Dashboard',
        description: 'Track real-time execution of portfolio commands with live status updates, progress indicators, and completion tracking across all your automated operations.',
        imageUrl: '/images/teasers/command-center-monitoring.jpg', // Fallback image
        component: ActionMonitoringDemo,
        mockData: {
          actions: [
            { id: '1', type: 'PROMOTE', strategy: 'Elite Scalper', status: 'EXECUTING', progress: 75, timestamp: '14:32:15' },
            { id: '2', type: 'DEMOTE', strategy: 'Beta Trader', status: 'COMPLETED', progress: 100, timestamp: '14:28:42' },
            { id: '3', type: 'SWAP', strategy: 'Grid Master', status: 'PENDING', progress: 0, timestamp: '14:35:01' }
          ]
        }
      },
      {
        title: 'Alert Center & Priority Actions',
        description: 'Stay informed with intelligent alerts for performance issues, risk thresholds, and system notifications. Prioritized by urgency with actionable insights.',
        imageUrl: '/images/teasers/command-center-alerts.jpg', // Fallback image
        component: AlertCenterDemo,
        mockData: {
          alerts: [
            { id: '1', priority: 'HIGH', type: 'PERFORMANCE', title: 'Strategy Underperforming', description: 'Beta Trader below target for 3 days', timestamp: '2 min ago', isNew: true },
            { id: '2', priority: 'MEDIUM', type: 'RISK', title: 'Risk Threshold Exceeded', description: 'Portfolio exposure at 89%', timestamp: '5 min ago' },
            { id: '3', priority: 'HIGH', type: 'SYSTEM', title: 'Account Rebalance Required', description: 'Elite Scalper allocation drift', timestamp: '8 min ago', isNew: true }
          ]
        }
      },
    ],
  },
  'Portfolio Forecast': {
    // heroBannerUrl removed
    title: 'See Your Trading Future with Statistical Precision', // Use placeholder for feature name
    description: 'Harness the power of Monte Carlo simulations to visualize thousands of potential futures for your portfolio. Understand your risk exposure with institutional-grade forecasting models. Exclusive to the {minPlan} plan.', // Use {minPlan}
    subFeatures: [
      {
        title: 'Monte Carlo Simulation Engine',
        description: 'Run thousands of statistical simulations using historical volatility patterns to visualize the probability distribution of your portfolio\'s future value with confidence intervals.',
        imageUrl: '/images/teasers/forecast-monte-carlo.jpg', // Fallback image
        component: MonteCarloSimulationDemo,
        mockData: {
          projections: mockMonteCarloData.projections.slice(0, 3),
          confidence: mockMonteCarloData.confidence
        }
      },
      {
        title: 'Interactive Forecast Configuration',
        description: 'Dynamically adjust forecast parameters with interactive controls for time horizons, capital amounts, account selection, and strategy filtering with real-time preview.',
        imageUrl: '/images/teasers/forecast-config.jpg', // Fallback image  
        component: ForecastConfigurationDemo,
        mockData: {
          config: {
            timeHorizon: 2,
            periodicity: 'daily',
            selectedAccounts: ['Elite Trading', 'Growth Portfolio'],
            selectedStrategies: ['Elite Scalper', 'Grid Master'],
            initialCapital: 50000
          }
        }
      },
      {
        title: 'Historical Performance Projection',
        description: 'Visualize animated forecast charts drawing profit projections based on historical performance with detailed metrics and growth trajectories.',
        imageUrl: '/images/teasers/forecast-projection.jpg', // Fallback image
        component: HistoricalProjectionDemo,
        mockData: {
          projection: {
            startValue: 50000,
            endValue: 127500,
            dataPoints: [
              { x: 0, y: 50000, label: 'Start' },
              { x: 25, y: 62000, label: '6M' },
              { x: 50, y: 78000, label: '1Y' },
              { x: 75, y: 98000, label: '18M' },
              { x: 100, y: 127500, label: '2Y' }
            ],
            profitMetrics: {
              totalProfit: 77500,
              profitPercentage: 155,
              annualizedReturn: 28.7
            }
          }
        }
      },
    ],
  },
  'Strategy Details': {
    // heroBannerUrl removed
    title: 'Professional-Grade Strategy Analytics', // Use placeholder for feature name
    description: 'Dissect every aspect of your trading strategies with institutional-grade analytics. Access professional metrics like Sharpe ratios, drawdown analysis, and trade-level investigation tools. Requires {minPlan} plan or higher.', // Use {minPlan}
    subFeatures: [
      {
        title: 'Strategy Performance X-Ray',
        description: 'Analyze professional metrics including Sharpe ratios, maximum drawdown, profit factors, and statistical significance across multiple timeframes with institutional precision.',
        imageUrl: '/images/teasers/strategy-metrics.jpg', // Fallback image
        component: StrategyXRayDemo,
        mockData: {
          strategy: mockStrategyHealthData.strategy,
          healthScore: mockStrategyHealthData.healthScore,
          components: mockStrategyHealthData.components,
          metrics: mockStrategyHealthData.metrics
        }
      },
      {
        title: 'Live Trading Activity Feed',
        description: 'Monitor real-time trade executions with detailed P&L tracking, market timing analysis, and live performance updates. See exactly when and how your strategies perform.',
        imageUrl: '/images/teasers/strategy-trades.jpg', // Fallback image
        component: TradingActivityFeedDemo,
        mockData: {
          trades: [
            { id: '1', strategy: 'Elite Scalper', symbol: 'BTCUSDT', side: 'BUY', quantity: 0.25, price: 43250.50, pnl: 127.45, timestamp: '14:32:15', status: 'FILLED', isNew: true },
            { id: '2', strategy: 'Grid Master', symbol: 'ETHUSDT', side: 'SELL', quantity: 1.8, price: 2580.30, pnl: -43.20, timestamp: '14:31:42', status: 'FILLED' },
            { id: '3', strategy: 'Momentum Pro', symbol: 'SOLUSDT', side: 'BUY', quantity: 25.0, price: 98.45, pnl: 89.15, timestamp: '14:30:33', status: 'PARTIAL' }
          ]
        }
      },
      {
        title: 'Strategy Health Analytics Dashboard',
        description: 'Comprehensive health distribution analysis showing strategy performance tiers, top performers, and portfolio-wide health trends with actionable insights.',
        imageUrl: '/images/teasers/strategy-health.jpg', // Fallback image
        component: HealthAnalyticsDashboardDemo,
        mockData: {
          analytics: {
            totalStrategies: 12,
            averageHealthScore: 87,
            distribution: { excellent: 4, good: 6, fair: 2, poor: 0 },
            topPerformer: { name: 'Elite Scalper', score: 94, trend: 'up' },
            portfolioTrend: 'up'
          }
        }
      },
    ],
  },
  'Portfolio Optimization': {
    title: 'Mathematical Portfolio Perfection',
    description: 'Apply Nobel Prize-winning optimization theories to maximize your risk-adjusted returns. Access institutional-grade portfolio optimization with live efficient frontier analysis and multiple mathematical models. Available on the {minPlan} plan and higher.',
    subFeatures: [
      {
        title: 'Smart Account Management',
        description: 'Optimize allocations within each trading account while respecting practical constraints and avoiding overexposure. Get the best of both worlds: mathematical precision and real-world practicality.',
        imageUrl: '/images/teasers/portfolio-opt-account-aware.jpg', // Fallback image
        component: SmartCapitalReallocationDemo,
        mockData: {
          allocations: mockOptimizationData.allocations,
          totalCapital: mockOptimizationData.metrics.totalCapital
        }
      },
      {
        title: 'Interactive Efficient Frontier',
        description: 'Explore the efficient frontier with interactive risk-return visualization, optimal point identification, and real-time Sharpe ratio calculations using Nobel Prize-winning theories.',
        imageUrl: '/images/teasers/portfolio-opt-frontier.jpg', // Fallback image
        component: InteractiveEfficientFrontierDemo,
        mockData: {
          optimization: {
            currentPoint: { risk: 18.5, return: 22.3, sharpe: 1.21 },
            optimalPoint: { risk: 14.2, return: 24.8, sharpe: 1.75 },
            frontierPoints: [
              { risk: 10, return: 15 },
              { risk: 12, return: 18 },
              { risk: 14.2, return: 24.8 },
              { risk: 18, return: 28 },
              { risk: 22, return: 30 }
            ]
          }
        }
      },
      {
        title: 'Strategy Lifecycle Visualization',
        description: 'Monitor strategy maturity stages with adaptive lifecycle management, showing emerging, growing, mature, and declining phases with intelligent reallocation.',
        imageUrl: '/images/teasers/portfolio-opt-lifecycle.jpg', // Fallback image
        component: LifecycleVisualizationDemo,
        mockData: {
          strategies: [
            { name: 'Momentum Pro', stage: 'Growing', progress: 75, allocation: 35, performance: 18.7 },
            { name: 'Elite Scalper', stage: 'Mature', progress: 95, allocation: 40, performance: 24.3 },
            { name: 'Grid Master', stage: 'Emerging', progress: 40, allocation: 15, performance: 12.1 },
            { name: 'Beta Trader', stage: 'Declining', progress: 25, allocation: 10, performance: -5.8 }
          ]
        }
      },
    ],
  },
  'Default': {
    // heroBannerUrl removed
    title: 'Unlock More Features', // Generic title
    description: 'Upgrade your plan to access advanced features like {featureName} and gain deeper insights into your trading performance. Requires {minPlan} plan or higher.', // Use {minPlan} and {featureName}
    subFeatures: [
      {
        title: 'Enhanced Analytics',
        description: 'Get more detailed reports and visualizations.',
        imageUrl: '/images/teasers/default-analytics.jpg', // Replace with actual image path
      },
      {
        title: 'Advanced Management Tools',
        description: 'Take greater control over your portfolio.',
        imageUrl: '/images/teasers/default-tools.jpg', // Replace with actual image path
      },
    ],
  }
};